// Handoff Sink Analysis — when a bot escalates, where does the call land?
//
// Joins contactAIAgentSession (bot session + outcome) → taskDetails (final
// queue/agent/termination) via interactionId. The result tells supervisors
// "when PatientType hands off, 62% go to Queue A, 23% to Queue B, 15% to a
// direct agent transfer" — the piece FlowFunnel + QueueHealth can't show.
//
// Pure-client aggregation: no new MCP calls, no fan-out. The sessions +
// tasks + legs are already fetched by useReportWindow for the window.

import React from 'react';
import { Badge, Group, Progress, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';

const REPORT_ID = 'handoff-sink';

// Which bot session outcomes count as "escalated to a human"? Primary case
// is ESCALATED; some tenants also surface FAILED or ABANDONED routing to a
// queue, but a conservative first cut sticks with ESCALATED only.
const HANDOFF_OUTCOMES = new Set(['ESCALATED']);

const COLUMNS = [
  {
    id: 'bot',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.bot || <em>(unnamed)</em>}</Text>,
  },
  {
    id: 'destination',
    label: 'Destination',
    pinned: true,
    render: (r) => (
      <div>
        <Text size="sm">{r.destination}</Text>
        {r.destinationType && <Text size="xs" c="dimmed">{r.destinationType}</Text>}
      </div>
    ),
  },
  {
    id: 'handoffs',
    label: 'Handoffs',
    hint: 'Count of ESCALATED bot sessions that landed on this destination.',
    align: 'right',
    render: (r) => <Text size="sm" fw={600}>{r.handoffs}</Text>,
  },
  {
    id: 'shareOfBot',
    label: '% of bot handoffs',
    hint: 'This destination\'s share of the bot\'s total handoffs.',
    align: 'right',
    render: (r) => <PctBadge pct={r.shareOfBot} color="blue" />,
  },
  {
    id: 'handledAtDest',
    label: 'Handled at dest',
    hint: 'Share of landings where the destination task was isContactHandled=true.',
    align: 'right',
    render: (r) => <PctBadge pct={r.handledPct} color="teal" />,
  },
  {
    id: 'avgWrapCode',
    label: 'Top wrap-up code',
    hint: 'Most-common lastWrapupCodeName across landings.',
    render: (r) => <Text size="xs">{r.topWrapCode || '—'}</Text>,
  },
  {
    id: 'topTermination',
    label: 'Top termination',
    hint: 'Most-common terminationReason across landings.',
    render: (r) => <Text size="xs">{r.topTermination || '—'}</Text>,
  },
  {
    id: 'mix',
    label: 'Mix',
    hint: 'Handled / transferred / abandoned at the destination.',
    width: 160,
    render: (r) => (
      <Tooltip label={`${r.handledPct}% handled · ${r.transferPct}% transferred · ${r.abandonPct}% abandoned`}>
        <Progress.Root size="md">
          {r.handledPct > 0 && <Progress.Section value={r.handledPct} color="teal" />}
          {r.transferPct > 0 && <Progress.Section value={r.transferPct} color="violet" />}
          {r.abandonPct > 0 && <Progress.Section value={r.abandonPct} color="orange" />}
        </Progress.Root>
      </Tooltip>
    ),
  },
];

const DEFAULT_VISIBLE = ['handoffs', 'shareOfBot', 'handledAtDest', 'avgWrapCode', 'topTermination', 'mix'];

export function HandoffSinkAnalysis({ data }) {
  const rows = buildRows(data.sessions ?? [], data.tasks ?? []);
  const uniqBots = new Set(rows.map((r) => r.bot)).size;
  const totalHandoffs = rows.reduce((n, r) => n + r.handoffs, 0);
  const csvRows = rows.map((r) => ({
    bot: r.bot,
    destination: r.destination,
    destination_type: r.destinationType ?? '',
    handoffs: r.handoffs,
    share_of_bot_handoffs_pct: r.shareOfBot,
    handled_at_dest_pct: r.handledPct,
    transferred_at_dest_pct: r.transferPct,
    abandoned_at_dest_pct: r.abandonPct,
    top_wrap_code: r.topWrapCode ?? '',
    top_termination_reason: r.topTermination ?? '',
  }));
  return (
    <ReportTable
      title="Handoff sink analysis"
      subtitle={`${totalHandoffs} ESCALATED bot session${totalHandoffs === 1 ? '' : 's'} across ${uniqBots} bot${uniqBots === 1 ? '' : 's'} · joined to taskDetails final queue/agent`}
      reportId={REPORT_ID}
      columns={COLUMNS}
      defaultVisibleIds={DEFAULT_VISIBLE}
      rows={rows}
      emptyLabel="No bot handoffs recorded in this window."
      csvExport={{ filename: `handoff-sink-${new Date().toISOString().slice(0, 10)}.csv`, rows: csvRows }}
    />
  );
}

function buildRows(sessions, tasks) {
  const taskByInteraction = new Map();
  for (const t of tasks) {
    if (t?.id) taskByInteraction.set(t.id, t);
  }

  // bucket = `${bot}||${destinationKey}`
  const buckets = new Map();
  // per-bot totals for "share of bot handoffs"
  const botTotals = new Map();

  for (const s of sessions) {
    if (!HANDOFF_OUTCOMES.has(s.interactionOutcome)) continue;
    const bot = s.agentName ?? '(unnamed)';
    botTotals.set(bot, (botTotals.get(bot) ?? 0) + 1);

    const task = taskByInteraction.get(s.interactionId);
    const { destination, destinationType } = resolveDestination(task);
    const bucketKey = `${bot}||${destination}`;

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        key: bucketKey,
        bot, destination, destinationType,
        handoffs: 0,
        handled: 0,
        transferred: 0,
        abandoned: 0,
        wrapCodes: new Map(),
        terminations: new Map(),
      });
    }
    const b = buckets.get(bucketKey);
    b.handoffs += 1;

    if (task) {
      if (task.isContactHandled) b.handled += 1;
      const tt = (task.terminationType ?? '').toUpperCase();
      const tr = (task.terminationReason ?? '').toLowerCase();
      if (tr.includes('transfer') || tr.includes('consult') || tt === 'TRANSFERRED') b.transferred += 1;
      if (tr.includes('abandon') || tt === 'ABANDONED') b.abandoned += 1;
      bumpMap(b.wrapCodes, task.lastWrapupCodeName);
      bumpMap(b.terminations, task.terminationReason);
    }
  }

  return [...buckets.values()]
    .map((b) => ({
      key: b.key,
      bot: b.bot,
      destination: b.destination,
      destinationType: b.destinationType,
      handoffs: b.handoffs,
      shareOfBot: pct(b.handoffs, botTotals.get(b.bot) ?? 0),
      handledPct: pct(b.handled, b.handoffs),
      transferPct: pct(b.transferred, b.handoffs),
      abandonPct: pct(b.abandoned, b.handoffs),
      topWrapCode: topKey(b.wrapCodes),
      topTermination: topKey(b.terminations),
    }))
    .sort((a, b) => (a.bot === b.bot ? b.handoffs - a.handoffs : a.bot.localeCompare(b.bot)));
}

function resolveDestination(task) {
  if (!task) return { destination: '(no matching task record)', destinationType: 'unresolved' };
  if (task.lastQueue?.name) return { destination: task.lastQueue.name, destinationType: 'Queue' };
  if (task.firstQueueName) return { destination: task.firstQueueName, destinationType: 'Queue (first)' };
  if (task.lastAgent?.name) return { destination: task.lastAgent.name, destinationType: 'Direct to agent' };
  if (task.entryPoint?.name) return { destination: task.entryPoint.name, destinationType: 'Entry point' };
  return { destination: '(no destination data)', destinationType: 'unknown' };
}

function bumpMap(map, key) {
  if (!key) return;
  map.set(key, (map.get(key) ?? 0) + 1);
}
function topKey(map) {
  let best = null, bestN = 0;
  for (const [k, n] of map) if (n > bestN) { best = k; bestN = n; }
  return best;
}
function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }

function PctBadge({ pct, color }) {
  if (pct == null || Number.isNaN(pct)) return <Text size="xs" c="dimmed">—</Text>;
  const variant = pct >= 20 ? 'filled' : 'light';
  return <Badge size="sm" color={color} variant={variant}>{pct}%</Badge>;
}
